/**
 * Image Compression Utility
 *
 * Compresses images on the client-side before uploading to reduce
 * file size and prevent memory issues on mobile devices.
 */

/**
 * Compress an image file to be under a target size
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxSizeMB - Maximum file size in MB (default: 0.8)
 * @param {number} options.maxWidthOrHeight - Maximum width or height (default: 1920)
 * @param {number} options.initialQuality - Initial compression quality (default: 0.8)
 * @returns {Promise<File>} Compressed image file
 */
export async function compressImage(file, options = {}) {
  const {
    maxSizeMB = 0.8, // Target 800KB (balance between quality and size)
    maxWidthOrHeight = 1920,
    initialQuality = 0.8,
  } = options;

  // If file is already small enough, return as-is
  const fileSizeMB = file.size / 1024 / 1024;
  if (fileSizeMB <= maxSizeMB) {
    console.log(`Image is already small enough (${fileSizeMB.toFixed(2)}MB)`);
    return file;
  }

  console.log(
    `Compressing image from ${fileSizeMB.toFixed(2)}MB to <${maxSizeMB}MB`,
  );

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > maxWidthOrHeight || height > maxWidthOrHeight) {
          if (width > height) {
            height = (height / width) * maxWidthOrHeight;
            width = maxWidthOrHeight;
          } else {
            width = (width / height) * maxWidthOrHeight;
            height = maxWidthOrHeight;
          }
        }

        // Create canvas and draw resized image
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Try different quality levels until we get under target size
        let quality = initialQuality;
        const attemptCompression = (currentQuality) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Failed to compress image"));
                return;
              }

              const compressedSizeMB = blob.size / 1024 / 1024;

              // If still too large and quality can be reduced further, try again
              if (compressedSizeMB > maxSizeMB && currentQuality > 0.1) {
                console.log(
                  `Size: ${compressedSizeMB.toFixed(2)}MB with quality ${currentQuality.toFixed(2)}, trying lower quality...`,
                );
                attemptCompression(currentQuality - 0.1);
                return;
              }

              // Create new File object from blob
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });

              console.log(
                `Compression complete: ${fileSizeMB.toFixed(2)}MB → ${compressedSizeMB.toFixed(2)}MB ` +
                  `(${((1 - compressedSizeMB / fileSizeMB) * 100).toFixed(1)}% reduction)`,
              );

              resolve(compressedFile);
            },
            "image/jpeg",
            currentQuality,
          );
        };

        attemptCompression(quality);
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = event.target.result;
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Get human-readable file size
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size string
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}
